/**
 * Client-side helpers for the recovery-codes display step of MFA
 * setup. Kept out of the page component so the PDF/clipboard logic
 * can be unit-tested in isolation.
 */
import { jsPDF } from 'jspdf';

import { CLIENT_NAME } from './branding.js';

/**
 * Build a one-page PDF containing the 10 recovery codes, the
 * generation timestamp, and a confidentiality notice. Triggers a
 * browser download via the synthetic-anchor pattern (jsPDF's `save()`
 * does this internally; we expose this wrapper so tests can mock
 * jsPDF cleanly).
 */
export function downloadRecoveryCodesPdf(codes: string[], generatedAt: Date = new Date()): void {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const marginX = 56;
  let y = 72;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text(`${CLIENT_NAME} — Codes de récupération`, marginX, y);
  y += 24;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(110, 110, 110);
  doc.text(`Généré le ${formatDateFr(generatedAt)}`, marginX, y);
  y += 28;

  doc.setTextColor(20, 20, 20);
  doc.setFontSize(11);
  const wrapWidth = 595 - marginX * 2;
  const intro = doc.splitTextToSize(
    'Chaque code ne peut être utilisé qu’une seule fois pour accéder à votre compte ' +
      'si vous perdez votre application d’authentification. Conservez ce document en ' +
      'lieu sûr et confidentiel.',
    wrapWidth,
  ) as string[];
  doc.text(intro, marginX, y);
  y += intro.length * 14 + 18;

  doc.setFont('courier', 'bold');
  doc.setFontSize(13);
  // Two columns of 5 codes — same layout as the on-screen grid so
  // the printed sheet matches what the user saw at activation.
  const halfWidth = (595 - marginX * 2) / 2;
  codes.forEach((code, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    doc.text(code, marginX + col * halfWidth, y + row * 24);
  });
  y += Math.ceil(codes.length / 2) * 24 + 24;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(140, 140, 140);
  doc.text(`${CLIENT_NAME} · Document confidentiel — ne pas partager.`, marginX, y);

  doc.save(`${CLIENT_NAME.toLowerCase()}-recovery-codes.pdf`);
}

/**
 * Copy the recovery codes to the system clipboard, one per line,
 * newline-separated. Returns true on success, false if the Clipboard
 * API is unavailable or write was rejected.
 */
export async function copyRecoveryCodes(codes: string[]): Promise<boolean> {
  if (!navigator.clipboard?.writeText) return false;
  try {
    await navigator.clipboard.writeText(codes.join('\n'));
    return true;
  } catch {
    return false;
  }
}

function formatDateFr(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} à ${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}
